/*
 * YourXI Bespoke XI: tools (undo/redo, share image, Surprise me, Make another + My XIs)
 * ------------------------------------------------------------------------------------
 * window.YXITools
 *
 *   History
 *     createHistory(getState, setState, {limit:50, coalesceMs:900, onChange})
 *       -> {push(opts), undo(), redo(), canUndo, canRedo, size, index, reset(), clear(),
 *           onChange(fn), bindKeys(target?, {inInputs:true}) -> unbind}
 *       push() after every change. Rapid edits to the same text field (same single string
 *       path, inside coalesceMs) merge into one step. push({key:'title'}) forces a merge key,
 *       push({coalesce:false}) forces a new step. Pushes made while undo/redo applies are ignored.
 *     mountHistoryButtons(el, history) -> {destroy()}
 *
 *   Share image
 *     makeShareImage(source, {format:'story'|'square'|'landscape', title, url:'yourxi.co.uk'}) -> Promise<Blob>
 *       source: Blob (best: wizard getPreviewPNG()), canvas, <svg>, <img>, SVG string, URL, or a Promise of any.
 *       story 1080x1920, square 1080x1080, landscape 1200x630.
 *     share(blob, {text, url, title, filename, silent}) -> Promise<{method:'share'|'download'|'cancelled', copied}>
 *       Web Share with files where supported, else download + copy link.
 *     shareLink(state, base?) -> Promise<string>   same #xi= encoding as the wizard
 *
 *   Surprise me
 *     surpriseXI({formation, format, theme, seed, variety, subs}) -> Promise<statePatch>
 *       theme: 'all-time' | 'club:<teamId>' | 'nation:<name>' | 'decade:1990s'
 *       patch.meta (not enumerable): {seed, theme, picks:[{slot, role, fit, score}]}
 *     surpriseThemes() -> [{id, label, group}]
 *     config({dataBase, fallbackBase})  where to load players data from
 *
 *   Make another / My XIs
 *     duplicateXI(state, {store:true}) -> new state, title "Copy of ...", both kept in My XIs
 *     saveXI(state) -> entry, listXIs(), openXI(id) -> state, deleteXI(id)
 *     mountMyXIs(el, {onOpen(state, entry), onDelete(entry), emptyText}) -> {refresh(), destroy()}
 *
 *   toast(msg)
 */
(function (root) {
  'use strict';

  var doc = root.document;
  var SCRIPT_SRC = (doc && doc.currentScript && doc.currentScript.src) || (root.location ? root.location.href : '');
  var CFG = {
    dataBase: resolve('../data-next/players/'),
    fallbackBase: resolve('../wizard/data/')
  };
  function resolve(rel) { try { return new URL(rel, SCRIPT_SRC).href; } catch (e) { return rel; } }
  function config(o) { o = o || {}; for (var k in o) CFG[k] = o[k]; return Object.assign({}, CFG); }

  var clone = function (x) { return x == null ? x : JSON.parse(JSON.stringify(x)); };
  var esc = function (s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); };

  /* =====================================================================
     Toast
     ===================================================================== */
  var _toastEl = null, _toastT = 0;
  function toast(msg) {
    if (!doc) return;
    if (!_toastEl) { _toastEl = doc.createElement('div'); _toastEl.className = 'yxt-toast'; _toastEl.setAttribute('role', 'status'); _toastEl.setAttribute('aria-live', 'polite'); doc.body.appendChild(_toastEl); }
    _toastEl.textContent = msg; _toastEl.classList.add('is-on');
    clearTimeout(_toastT); _toastT = setTimeout(function () { _toastEl.classList.remove('is-on'); }, 2600);
  }

  /* =====================================================================
     1. History
     ===================================================================== */
  function leafDiff(a, b, path, out) {
    if (out.length > 3) return out;
    if (a === b) return out;
    var ta = typeof a, tb = typeof b;
    if (a && b && ta === 'object' && tb === 'object' && Array.isArray(a) === Array.isArray(b)) {
      var keys = {}; Object.keys(a).forEach(function (k) { keys[k] = 1; }); Object.keys(b).forEach(function (k) { keys[k] = 1; });
      Object.keys(keys).forEach(function (k) { leafDiff(a[k], b[k], path ? path + '.' + k : k, out); });
      return out;
    }
    out.push({ path: path, a: a, b: b });
    return out;
  }

  function createHistory(getState, setState, opts) {
    opts = opts || {};
    var limit = Math.max(2, opts.limit || 50), coalesceMs = opts.coalesceMs == null ? 900 : opts.coalesceMs;
    var stack = [], idx = -1, lastKey = null, lastT = 0, applying = false, listeners = [];
    if (typeof opts.onChange === 'function') listeners.push(opts.onChange);

    function snap() { var s = clone(getState()); return { state: s, json: JSON.stringify(s) }; }
    function emit() { var info = { canUndo: idx > 0, canRedo: idx < stack.length - 1, index: idx, size: stack.length }; listeners.forEach(function (fn) { try { fn(info); } catch (e) {} }); }
    function reset() { stack = [snap()]; idx = 0; lastKey = null; emit(); }
    function apply(i) {
      idx = i; lastKey = null; applying = true;
      try { setState(clone(stack[idx].state)); } finally { applying = false; }
      emit(); return true;
    }

    var api = {
      push: function (o) {
        o = o || {};
        if (applying) return false;
        var s = snap(), top = stack[idx];
        if (top && s.json === top.json) return false;
        var now = Date.now(), key = null;
        if (o.coalesce !== false) {
          if (o.key) key = String(o.key);
          else if (top) {
            var d = leafDiff(top.state, s.state, '', []);
            if (d.length === 1 && (typeof d[0].b === 'string' || typeof d[0].a === 'string')) key = 'text:' + d[0].path;
          }
        }
        if (key && key === lastKey && now - lastT < coalesceMs && idx > 0 && idx === stack.length - 1) {
          stack[idx] = s;                                  // merge into the open typing step
        } else {
          stack = stack.slice(0, idx + 1); stack.push(s); idx = stack.length - 1;
          while (stack.length > limit) { stack.shift(); idx--; }
        }
        lastKey = key; lastT = now; emit(); return true;
      },
      undo: function () { return idx > 0 ? apply(idx - 1) : false; },
      redo: function () { return idx < stack.length - 1 ? apply(idx + 1) : false; },
      reset: reset,
      clear: reset,
      onChange: function (fn) { listeners.push(fn); return function () { listeners = listeners.filter(function (f) { return f !== fn; }); }; },
      bindKeys: function (target, ko) {
        target = target || root; ko = ko || {};
        var inInputs = ko.inInputs !== false;
        function onKey(e) {
          var mod = e.metaKey || e.ctrlKey; if (!mod || e.altKey) return;
          var k = (e.key || '').toLowerCase();
          var redo = (k === 'z' && e.shiftKey) || (k === 'y' && e.ctrlKey && !e.metaKey);
          var undo = k === 'z' && !e.shiftKey;
          if (!undo && !redo) return;
          var t = e.target, typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
          if (typing && !inInputs) return;
          if (undo ? api.canUndo : api.canRedo) { e.preventDefault(); undo ? api.undo() : api.redo(); }
          else if (!typing) e.preventDefault();
        }
        target.addEventListener('keydown', onKey);
        return function () { target.removeEventListener('keydown', onKey); };
      }
    };
    Object.defineProperties(api, {
      canUndo: { get: function () { return idx > 0; } },
      canRedo: { get: function () { return idx < stack.length - 1; } },
      size: { get: function () { return stack.length; } },
      index: { get: function () { return idx; } }
    });
    reset();
    return api;
  }

  function mountHistoryButtons(el, h) {
    var isMac = /Mac|iPhone|iPad/.test((root.navigator && root.navigator.platform) || '');
    el.classList.add('yxt-hist');
    el.innerHTML =
      '<button type="button" class="yxt-btn yxt-btn--ghost yxt-hist-b" data-a="undo" title="Undo (' + (isMac ? 'Cmd' : 'Ctrl') + ' Z)">' + icon('undo') + '<span>Undo</span></button>' +
      '<button type="button" class="yxt-btn yxt-btn--ghost yxt-hist-b" data-a="redo" title="Redo (' + (isMac ? 'Shift Cmd' : 'Shift Ctrl') + ' Z)">' + icon('redo') + '<span>Redo</span></button>';
    var bU = el.querySelector('[data-a="undo"]'), bR = el.querySelector('[data-a="redo"]');
    function sync() { bU.disabled = !h.canUndo; bR.disabled = !h.canRedo; }
    function onClick(e) { var b = e.target.closest('button'); if (!b) return; b.dataset.a === 'undo' ? h.undo() : h.redo(); }
    el.addEventListener('click', onClick);
    var off = h.onChange(sync); sync();
    return { destroy: function () { off(); el.removeEventListener('click', onClick); el.innerHTML = ''; } };
  }
  function icon(n) {
    var d = n === 'undo' ? 'M9 7H4V2M4.5 7A8 8 0 1 1 4 12' : 'M15 7h5V2M19.5 7A8 8 0 1 0 20 12';
    return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="square" aria-hidden="true"><path d="' + d + '"/></svg>';
  }

  /* =====================================================================
     2. Share image
     ===================================================================== */
  var SIZES = { story: [1080, 1920], square: [1080, 1080], landscape: [1200, 630] };
  var C = { bg: '#F2F1EE', ink: '#111111', muted: '#62625F', line: '#E4E2DD', frame: '#151515' };
  var SHIRT_PATH = 'M30 6 Q50 18 70 6 L96 22 L86 44 L74 38 L74 88 L26 88 L26 38 L14 44 L4 22 Z';

  function loadImg(src) {
    return new Promise(function (res, rej) { var i = new Image(); i.decoding = 'async'; i.onload = function () { res(i); }; i.onerror = function () { rej(new Error('Could not read the preview image')); }; i.src = src; });
  }
  // duck-typed so a Blob or element from another frame (the wizard in an iframe) still works
  function tagOf(x) { return x && typeof x === 'object' && x.nodeType === 1 ? String(x.tagName).toLowerCase() : ''; }
  function isBlob(x) { return x && typeof x === 'object' && typeof x.arrayBuffer === 'function' && typeof x.size === 'number' && 'type' in x; }
  async function toDrawable(src) {
    src = await src;
    if (!src) throw new Error('No preview to draw');
    var tag = tagOf(src);
    if (tag === 'canvas') return { img: src, w: src.width, h: src.height };
    if (tag === 'img') { if (!src.complete) await new Promise(function (r) { src.onload = r; }); return { img: src, w: src.naturalWidth, h: src.naturalHeight }; }
    if (isBlob(src)) {
      var u = URL.createObjectURL(src instanceof Blob ? src : new Blob([await src.arrayBuffer()], { type: src.type || 'image/png' }));
      try { var im = await loadImg(u); return { img: im, w: im.naturalWidth, h: im.naturalHeight, revoke: u }; } catch (e) { URL.revokeObjectURL(u); throw e; }
    }
    if (tag === 'svg') {
      var vb = src.viewBox && src.viewBox.baseVal, w = (vb && vb.width) || src.clientWidth || 900, h = (vb && vb.height) || src.clientHeight || 1200;
      var c2 = src.cloneNode(true); c2.setAttribute('width', w); c2.setAttribute('height', h); c2.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      return toDrawable(new Blob([new XMLSerializer().serializeToString(c2)], { type: 'image/svg+xml' }));
    }
    if (typeof src === 'string') {
      if (/^\s*<svg/i.test(src)) return toDrawable(new Blob([src], { type: 'image/svg+xml' }));
      var i2 = await loadImg(src); return { img: i2, w: i2.naturalWidth, h: i2.naturalHeight };
    }
    throw new Error('Unsupported preview source');
  }
  async function ensureFonts() {
    if (!doc || !doc.fonts || !doc.fonts.load) return;
    try {
      await Promise.race([
        Promise.all(['800 40px Montserrat', '300 40px Montserrat', '500 40px Geist', '400 40px Geist'].map(function (f) { return doc.fonts.load(f); })),
        new Promise(function (r) { setTimeout(r, 2500); })
      ]);
    } catch (e) {}
  }
  var UI = '"Geist", system-ui, -apple-system, "Segoe UI", sans-serif', MARK = '"Montserrat", "Geist", system-ui, sans-serif';

  function drawMark(ctx, cx, cy, size, align) {
    // shirt icon + YOUR (800) XI (300), like the site header
    ctx.save();
    ctx.textBaseline = 'alphabetic'; ctx.textAlign = 'left';
    ctx.font = '800 ' + size + 'px ' + MARK; var wA = ctx.measureText('YOUR').width;
    ctx.font = '300 ' + size + 'px ' + MARK; var wB = ctx.measureText('XI').width;
    var iw = size * 1.05, ih = iw * 0.9, gap = size * 0.4, total = iw + gap + wA + wB;
    var x0 = align === 'left' ? cx : cx - total / 2, base = cy + size * 0.36;
    ctx.translate(x0, base - ih * 0.93); ctx.scale(iw / 100, ih / 90);
    ctx.fillStyle = C.ink; ctx.fill(new Path2D(SHIRT_PATH));
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = C.ink;
    ctx.font = '800 ' + size + 'px ' + MARK; ctx.fillText('YOUR', x0 + iw + gap, base);
    ctx.font = '300 ' + size + 'px ' + MARK; ctx.fillText('XI', x0 + iw + gap + wA, base);
    ctx.restore();
    return total;
  }
  // wrap into lines, balanced, never leaving one word alone on the last line
  function wrapLines(ctx, text, maxW, maxLines) {
    var words = String(text || '').replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
    if (!words.length) return [];
    function greedy(w) { var out = [], line = ''; words.forEach(function (x) { var t = line ? line + ' ' + x : x; if (line && ctx.measureText(t).width > w) { out.push(line); line = x; } else line = t; }); if (line) out.push(line); return out; }
    var base = greedy(maxW), n = base.length, best = base;
    for (var w = maxW; w > maxW * 0.4; w -= 8) {
      var ls = greedy(w); if (ls.length !== n) break;
      if (ls.length > 1 && ls[ls.length - 1].indexOf(' ') < 0) continue;
      best = ls;
    }
    if (best.length > 1 && best[best.length - 1].indexOf(' ') < 0) {     // still a widow: pull a word down
      var prev = best[best.length - 2].split(' ');
      if (prev.length > 2) { best[best.length - 1] = prev.pop() + ' ' + best[best.length - 1]; best[best.length - 2] = prev.join(' '); }
    }
    if (best.length > maxLines) { best = best.slice(0, maxLines); best[maxLines - 1] = best[maxLines - 1].replace(/\s*\S*$/, '') + '...'; }
    return best;
  }
  function framedPrint(ctx, d, cx, top, maxW, maxH, frameW) {
    var r = Math.min(maxW / d.w, maxH / d.h), w = Math.round(d.w * r), h = Math.round(d.h * r);
    var x = Math.round(cx - w / 2 - frameW), y = Math.round(top);
    ctx.save();
    ctx.shadowColor = 'rgba(17,17,17,.16)'; ctx.shadowBlur = frameW * 3; ctx.shadowOffsetY = frameW * 1.4;
    ctx.fillStyle = C.frame; ctx.fillRect(x, y, w + frameW * 2, h + frameW * 2);
    ctx.restore();
    ctx.fillStyle = '#fff'; ctx.fillRect(x + frameW, y + frameW, w, h);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(d.img, x + frameW, y + frameW, w, h);
    return { x: x, y: y, w: w + frameW * 2, h: h + frameW * 2 };
  }
  function centreText(ctx, lines, cx, y, lh) { lines.forEach(function (l, i) { ctx.fillText(l, cx, y + i * lh); }); }

  async function makeShareImage(source, o) {
    o = o || {};
    var fmt = SIZES[o.format] ? o.format : 'story', W = SIZES[fmt][0], H = SIZES[fmt][1];
    var url = (o.url || 'yourxi.co.uk').replace(/^https?:\/\//, '').replace(/\/$/, '');
    var line = o.line || ('Build yours at ' + url);
    var title = (o.title || '').trim();
    var d = await toDrawable(source);
    await ensureFonts();
    var cv = doc.createElement('canvas'); cv.width = W; cv.height = H;
    var ctx = cv.getContext('2d');
    ctx.fillStyle = C.bg; ctx.fillRect(0, 0, W, H);
    ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
    if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';

    if (fmt === 'story') {
      drawMark(ctx, W / 2, 150, 44);
      var hasTitle = !!title;
      var printTop = 246, printMaxH = hasTitle ? 1240 : 1380;
      var box = framedPrint(ctx, d, W / 2, printTop, 924, printMaxH, 18);
      var y = box.y + box.h + 118;
      if (hasTitle) {
        ctx.fillStyle = C.ink; ctx.font = '500 58px ' + UI;
        if ('letterSpacing' in ctx) ctx.letterSpacing = '-1.5px';
        var tl = wrapLines(ctx, title, 900, 2); centreText(ctx, tl, W / 2, y, 70); y += tl.length * 70 + 4;
        if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';
      }
      ctx.fillStyle = C.muted; ctx.font = '400 34px ' + UI; ctx.fillText(line, W / 2, H - 96);
      ctx.fillStyle = C.line; ctx.fillRect(W / 2 - 40, H - 176, 80, 2);
    } else if (fmt === 'square') {
      drawMark(ctx, W / 2, 74, 32);
      framedPrint(ctx, d, W / 2, 128, 700, 800, 13);
      ctx.fillStyle = C.muted; ctx.font = '400 28px ' + UI; ctx.fillText(line, W / 2, H - 42);
    } else {
      // landscape (link previews): mark + title left, print centred, line right
      framedPrint(ctx, d, W / 2, 34, 520, 534, 10);
      ctx.textAlign = 'left';
      drawMark(ctx, 64, 92, 26, 'left');
      if (title) {
        ctx.fillStyle = C.ink; ctx.font = '500 34px ' + UI;
        if ('letterSpacing' in ctx) ctx.letterSpacing = '-0.8px';
        var tl2 = wrapLines(ctx, title, 300, 4); tl2.forEach(function (l, i) { ctx.fillText(l, 64, 300 + i * 42 - (tl2.length - 1) * 21); });
        if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';
      }
      ctx.textAlign = 'right'; ctx.fillStyle = C.muted; ctx.font = '400 24px ' + UI;
      var ll = wrapLines(ctx, line, 280, 2); ll.forEach(function (l, i) { ctx.fillText(l, W - 64, H - 64 - (ll.length - 1 - i) * 32); });
    }
    if (d.revoke) URL.revokeObjectURL(d.revoke);
    return await new Promise(function (res, rej) { cv.toBlob(function (b) { b ? res(b) : rej(new Error('Could not make the image')); }, 'image/png'); });
  }

  async function copyText(text) {
    if (!text) return false;
    try { if (root.navigator.clipboard && root.isSecureContext !== false) { await root.navigator.clipboard.writeText(text); return true; } } catch (e) {}
    try {
      var ta = doc.createElement('textarea'); ta.value = text; ta.setAttribute('readonly', ''); ta.style.cssText = 'position:fixed;left:-9999px;top:0';
      doc.body.appendChild(ta); ta.select(); var ok = doc.execCommand('copy'); ta.remove(); return !!ok;
    } catch (e) { return false; }
  }
  function download(blob, name) {
    var u = URL.createObjectURL(blob), a = doc.createElement('a');
    a.href = u; a.download = name; a.rel = 'noopener'; doc.body.appendChild(a); a.click(); a.remove();
    setTimeout(function () { URL.revokeObjectURL(u); }, 4000);
  }
  async function share(blob, o) {
    o = o || {};
    var url = o.url || 'https://yourxi.co.uk';
    var name = o.filename || 'yourxi.png';
    var text = o.text || 'My all-time XI. Build yours at yourxi.co.uk';
    var nav = root.navigator, file = null;
    try { file = new File([blob], name, { type: blob.type || 'image/png' }); } catch (e) {}
    if (file && nav.share && nav.canShare && nav.canShare({ files: [file] })) {
      try { await nav.share({ files: [file], text: text + (url && text.indexOf(url) < 0 ? ' ' + url : ''), title: o.title || 'My XI' }); return { method: 'share', copied: false }; }
      catch (e) { if (e && e.name === 'AbortError') return { method: 'cancelled', copied: false }; }
    }
    download(blob, name);
    var copied = await copyText(url);
    if (!o.silent) toast(copied ? 'Image saved. Link copied, paste it with your post.' : 'Image saved to your downloads.');
    return { method: 'download', copied: copied };
  }

  // share link, same encoding as the wizard (packState v1, deflate-raw + base64url)
  var KIT_KEYS = ['pat', 'scale', 'body', 'sleeves', 'pc', 'collar', 'cc', 'cuffs', 'trim', 'long', 'variant'];
  function packKit(k) { return k ? KIT_KEYS.map(function (f) { return f === 'long' ? (k.long ? 1 : 0) : typeof k[f] === 'string' ? k[f].replace('#', '') : k[f]; }) : 0; }
  function packState(S) {
    var sl = function (x) { return x ? [x.name, x.first, x.last, x.pid || '', x.teamId || '', x.team || '', x.from || 0, x.to || 0, ({ home: 0, away: 1, third: 2 }[x.side] || 0), x.national ? 1 : 0, { home: 0, away: 1, own: 2 }[x.kit] || 0, x.kit === 'own' ? packKit(x.own) : 0] : 0; };
    var kits = S.kits ? [packKit(S.kits.home), packKit(S.kits.away), packKit(S.kits.gk), S.kits.gkAuto ? 1 : 0] : 0;
    var icon = S.icon || { id: 'none', metal: 'plain' };
    return [1, S.title || '', S.formation, (S.slots || []).map(sl), (S.subs || []).map(sl), S.manager || '', S.captain == null ? -1 : S.captain, S.message || '', S.mode === 'custom' ? 1 : S.mode === 'legends' ? 0 : -1, kits, { none: 0, message: 1, achievement: 2 }[S.right || 'message'], S.ach || ['', '', ''], [icon.id, icon.metal], S.format || '11', S.pitchStyle || 'classic', S.tint || ''];
  }
  function b64u(bytes) { var s = ''; bytes.forEach(function (b) { s += String.fromCharCode(b); }); return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''); }
  async function shareHash(S) {
    var json = new TextEncoder().encode(JSON.stringify(packState(S)));
    if (root.CompressionStream) {
      var cs = new Blob([json]).stream().pipeThrough(new CompressionStream('deflate-raw'));
      return 'z' + b64u(new Uint8Array(await new Response(cs).arrayBuffer()));
    }
    return 'j' + b64u(json);
  }
  async function shareLink(S, base) {
    base = base || 'https://yourxi.co.uk/pages/bespoke-xi';
    return base.replace(/#.*$/, '') + '#xi=' + (await shareHash(S));
  }

  /* =====================================================================
     3. Surprise me (legends mode)
     ===================================================================== */
  var DATA = null, _dataP = null;
  var _scriptP = {};
  function loadScript(src) {
    if (!_scriptP[src]) _scriptP[src] = new Promise(function (res, rej) {
      var el = doc.createElement('script'); el.src = src; el.async = true;
      el.onload = function () { res(); }; el.onerror = function () { delete _scriptP[src]; el.remove(); rej(new Error('load ' + src)); };
      doc.head.appendChild(el);
    });
    return _scriptP[src];
  }
  // load a data file that assigns window.YXI_INDEX without clobbering what the wizard already has
  async function loadIndexFrom(base) {
    var prev = root.YXI_INDEX, had = 'YXI_INDEX' in root;
    root.YXI_INDEX = undefined;
    try { await loadScript(base + 'index.js'); return root.YXI_INDEX; }
    finally { if (had) root.YXI_INDEX = prev; else { try { delete root.YXI_INDEX; } catch (e) { root.YXI_INDEX = undefined; } } }
  }
  // shard + club index callbacks are globals; route the ones we asked for to us, the rest to the wizard
  var _mine = { shard: {}, club: 0 }, _got = { shard: {}, club: null }, _wrapped = { shard: null, club: null };
  function hookGlobals() {
    if (root.YXI_SHARD !== _wrapped.shard) {
      var prevS = root.YXI_SHARD;
      _wrapped.shard = function (k, data) { if (_mine.shard[k]) { _mine.shard[k] = 0; _got.shard[k] = data; } else if (typeof prevS === 'function') prevS(k, data); };
      root.YXI_SHARD = _wrapped.shard;
    }
    if (root.YXI_CLUBIDX !== _wrapped.club) {
      var prevC = root.YXI_CLUBIDX;
      _wrapped.club = function (d) { if (_mine.club) { _mine.club = 0; _got.club = d; } else if (typeof prevC === 'function') prevC(d); };
      root.YXI_CLUBIDX = _wrapped.club;
    }
  }
  function parseIndex(ix, base) {
    var cols = ix.cols || ['q', 'name', 'short', 'pos', 'fine', 'nat', 'sl', 'years'];
    var c = {}; cols.forEach(function (n, i) { c[n] = i; });
    var greatness = cols.indexOf('fame') >= 0;               // data-next: sl is the greatness score, fame the sitelinks
    var players = ix.players.map(function (r, i) {
      var yrs = String(r[c.years] || '').match(/\d{4}/g) || [];
      return {
        q: r[c.q], id: 'Q' + r[c.q], name: r[c.name], short: r[c.short] || null,
        pos: String(r[c.pos] || '').split('/').filter(Boolean), fine: r[c.fine] ? String(r[c.fine]).split('/') : [],
        nat: ix.nats[r[c.nat]] || '', score: +r[c.sl] || 0, rank: i,
        y0: yrs.length ? +yrs[0] : null, y1: yrs.length ? +yrs[yrs.length - 1] : null
      };
    });
    var byQ = {}; players.forEach(function (p) { byQ[p.q] = p; });
    return { base: base, greatness: greatness, players: players, byQ: byQ, shards: {}, clubIdx: null };
  }
  function loadData() {
    if (DATA) return Promise.resolve(DATA);
    if (_dataP) return _dataP;
    _dataP = (async function () {
      hookGlobals();
      var ix = null, base = null;
      var cur = root.YXI_INDEX;
      if (cur && cur.cols && cur.cols.indexOf('fame') >= 0 && cur.players) { ix = cur; base = CFG.dataBase; }   // page already has data-next
      if (!ix) { try { ix = await loadIndexFrom(CFG.dataBase); base = CFG.dataBase; } catch (e) { ix = null; } }
      if (!ix && cur && cur.players) { ix = cur; base = CFG.fallbackBase; }
      if (!ix) { ix = await loadIndexFrom(CFG.fallbackBase); base = CFG.fallbackBase; }
      if (!ix || !ix.players) throw new Error('Players data not found');
      DATA = parseIndex(ix, base);
      return DATA;
    })();
    _dataP.catch(function () { _dataP = null; });
    return _dataP;
  }
  async function stintsOf(p) {
    if (p.stints) return p.stints;
    var k = p.q % 64;
    if (!DATA.shards[k]) {
      hookGlobals();
      _mine.shard[k] = 1;
      await loadScript(DATA.base + 's' + k + '.js');
      DATA.shards[k] = _got.shard[k] || {};
    }
    var rows = DATA.shards[k][p.q] || [];
    p.stints = rows.map(function (r) { return { teamId: 'Q' + r[0], team: r[1], from: r[2] || null, to: r[3] || null, national: !!r[4], apps: r[5] || 0 }; });
    return p.stints;
  }
  async function clubIndex() {
    if (DATA.clubIdx) return DATA.clubIdx;
    hookGlobals(); _mine.club = 1;
    await loadScript(DATA.base + 'clubs-index.js');
    DATA.clubIdx = _got.club || {};
    return DATA.clubIdx;
  }

  // seeded RNG
  function hashSeed(s) { s = String(s); var h = 2166136261; for (var i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
  function mulberry(a) { return function () { a |= 0; a = a + 0x6D2B79F5 | 0; var t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }

  // which fine roles can play which slot, and how well
  var ACCEPT = {
    GK: { GK: 1 },
    CB: { CB: 1, DM: 0.55, FB: 0.6 },
    FB: { FB: 1, WB: 1, CB: 0.62 },
    WB: { FB: 0.95, WB: 1, W: 0.8 },
    DM: { DM: 1, CM: 0.86, CB: 0.5 },
    CM: { CM: 1, DM: 0.86, AM: 0.84 },
    AM: { AM: 1, CM: 0.8, W: 0.72, ST: 0.6 },
    W: { W: 1, AM: 0.7, ST: 0.62 },
    ST: { ST: 1, W: 0.6, AM: 0.55 }
  };
  // players with only a coarse position: plausible, but a tagged specialist wins a close call
  var COARSE_ACCEPT = {
    GK: { GK: 1 },
    DF: { CB: 0.74, FB: 0.68, WB: 0.6 },
    MF: { CM: 0.74, DM: 0.64, AM: 0.66, W: 0.56 },
    FW: { ST: 0.78, W: 0.56, AM: 0.46 }
  };
  function isKeeper(p) { return p.pos[0] === 'GK' || p.fine.indexOf('GK') >= 0; }
  function fit(p, role) {
    var keeper = isKeeper(p);
    if (role === 'GK') return keeper ? 1 : 0;
    if (keeper || !p.pos.length) return 0;                                  // no keepers outfield
    var best = 0;
    if (p.fine.length) p.fine.forEach(function (f, i) { var v = (ACCEPT[role] || {})[f] || 0; best = Math.max(best, v * (1 - i * 0.1)); });
    else p.pos.forEach(function (c, i) { var v = ((COARSE_ACCEPT[c] || {})[role] || 0); best = Math.max(best, v * (1 - i * 0.15)); });
    return best;
  }

  // fine roles for the formation's slots (pitches.js when present, else the wizard's own rules)
  function rowsOf(f) { return [1].concat(String(f).split('-').map(Number).filter(function (n) { return n > 0; })); }
  function slotRoles(format, formation) {
    var px = root.YourXIPitches || root.YXIPitches;
    if (px && px.getFormat) {
      try {
        var F = px.getFormat(format || 'eleven');
        var fm = F && F.formations.filter(function (x) { return x.id === formation; })[0];
        if (fm) return { roles: fm.slots.map(function (s) { return s.fine || 'CM'; }), format: F.id, maxSubs: F.maxSubs || 5 };
      } catch (e) {}
    }
    var rows = rowsOf(formation), out = [];
    rows.forEach(function (c, r) {
      for (var k = 0; k < c; k++) {
        var end = (k === 0 || k === c - 1);
        if (r === 0) out.push('GK');
        else if (r === 1) out.push(c === 3 ? 'CB' : end ? (c === 5 ? 'WB' : 'FB') : 'CB');
        else if (r === rows.length - 1) out.push(c === 3 && end ? 'W' : 'ST');
        else if (formation === '4-2-3-1') out.push(r === 2 ? 'DM' : (end ? 'W' : 'AM'));
        else if (c === 5) out.push(end ? 'WB' : 'CM');
        else if (c === 4) out.push(end ? (formation === '3-4-3' ? 'WB' : 'W') : 'CM');
        else out.push('CM');
      }
    });
    return { roles: out, format: format || 'eleven', maxSubs: 5 };
  }

  function splitName(full, short) {
    full = (full || '').trim().split(',')[0].trim().replace(/\s+(Jr\.?|Jnr\.?|Sr\.?|Snr\.?|I{1,3}|IV)$/i, ''); // 17 Oct: suffixes never print as surname
    if (short && full.slice(-short.length) === short && full !== short) return { first: full.slice(0, full.length - short.length).trim(), last: short };
    var parts = full.split(/\s+/);
    if (parts.length === 1) return { first: '', last: parts[0] };
    var particles = ['van', 'von', 'de', 'da', 'di', 'del', 'der', 'dos', 'das', 'le', 'la', 'ter', 'ten', 'mac'];
    var i = parts.length - 1;
    while (i > 1 && particles.indexOf(parts[i - 1].toLowerCase()) >= 0) i--;
    return { first: parts.slice(0, i).join(' '), last: parts.slice(i).join(' ') };
  }
  function hasKit(teamId) {
    var cl = root.YXI_CLUBS; if (!cl) return false;
    if (!hasKit._m) { hasKit._m = {}; (cl.teams || cl).forEach(function (c) { hasKit._m[c.teamId] = c; }); }
    return !!hasKit._m[teamId];
  }
  function clubName(teamId) { hasKit(teamId); var c = hasKit._m && hasKit._m[teamId]; return c ? c.name : null; }

  // the stint we put on the print: their most famous one that fits the theme
  function pickStint(p, th) {
    var st = p.stints || [];
    var pool = st;
    if (th.kind === 'club') pool = st.filter(function (s) { return s.teamId === th.teamId; });
    else if (th.kind === 'nation') { pool = st.filter(function (s) { return s.national; }); }
    else {
      pool = st.filter(function (s) { return !s.national; });
      if (th.kind === 'decade') {
        var inDec = pool.filter(function (s) { return overlap(s.from || s.to, s.to || s.from, th.from, th.to) > 0; });
        if (inDec.length) pool = inDec;
      }
    }
    if (!pool.length) pool = st.filter(function (s) { return !s.national; });
    if (!pool.length) pool = st;
    if (!pool.length) return null;
    var best = null, bs = -1;
    pool.forEach(function (s) {
      var len = Math.max(0, (s.to || s.from || 0) - (s.from || s.to || 0)) + 1;
      var sc = (s.apps || 0) * 10 + len * 6 + (s.from || s.to ? 2 : 0);
      if (hasKit(s.teamId)) sc = sc * 1.35 + 40;                                      // a club we have the era kit for
      if (th.kind === 'decade') sc *= 1 + overlap(s.from || s.to, s.to || s.from, th.from, th.to) / 10;
      if (sc > bs) { bs = sc; best = s; }
    });
    // decade theme: show the year range inside the decade so the era kit matches
    if (best && th.kind === 'decade' && best.from && best.to) {
      var a = Math.max(best.from, th.from), b = Math.min(best.to, th.to);
      if (a <= b) best = Object.assign({}, best, { from: a, to: b });
    }
    return best;
  }
  function overlap(a0, a1, b0, b1) { if (a0 == null || a1 == null) return 0; return Math.max(0, Math.min(a1, b1) - Math.max(a0, b0) + 1); }

  function parseTheme(t) {
    t = String(t || 'all-time').trim();
    var m;
    if ((m = t.match(/^club:(Q?\d+)$/i))) return { kind: 'club', teamId: m[1][0] === 'Q' ? m[1] : 'Q' + m[1], id: t };
    if ((m = t.match(/^nation:(.+)$/i))) return { kind: 'nation', nat: m[1].trim(), id: t };
    if ((m = t.match(/^decade:(\d{4})s?$/i))) { var y = +m[1] - (+m[1] % 10); return { kind: 'decade', from: y, to: y + 9, id: 'decade:' + y + 's' }; }
    return { kind: 'all', id: 'all-time' };
  }
  function themeTitle(th, nationTeam) {
    if (th.kind === 'club') return (clubName(th.teamId) || 'Club') + ' All-Time XI';
    if (th.kind === 'nation') return (nationTeam || th.nat) + ' All-Time XI';
    if (th.kind === 'decade') return 'Best of the ' + th.from + 's';
    return 'All-Time Greats XI';
  }

  async function candidates(th) {
    var P = DATA.players, out = [];
    if (th.kind === 'club') {
      var ci = await clubIndex();
      var list = (ci[th.teamId.slice(1)] || []).map(function (q) { return DATA.byQ[q]; }).filter(Boolean);
      var top = list[0] ? list[0].score : 1;
      list.slice(0, 160).forEach(function (p, i) { out.push({ p: p, s: 100 / (i + 6) + 8 * p.score / (top || 1) }); });   // club order first, greatness second
    } else if (th.kind === 'nation') {
      var n = th.nat.toLowerCase();
      P.forEach(function (p) { if (out.length < 320 && p.nat.toLowerCase() === n) out.push({ p: p, s: p.score }); });
    } else if (th.kind === 'decade') {
      P.forEach(function (p) {
        if (out.length >= 380 || p.y0 == null) return;
        var ov = overlap(p.y0, p.y1, th.from, th.to);
        if (ov >= 4) out.push({ p: p, s: p.score * (0.7 + 0.3 * Math.min(ov, 10) / 10) });
      });
    } else {
      P.slice(0, 420).forEach(function (p) { out.push({ p: p, s: p.score }); });
    }
    return out;
  }

  async function surpriseXI(o) {
    o = o || {};
    await loadData();
    var th = parseTheme(o.theme);
    var seed = o.seed == null || o.seed === '' ? Math.floor(Math.random() * 1e9) : o.seed;
    var rnd = mulberry(hashSeed(seed + '|' + th.id + '|' + (o.formation || '')));
    var variety = o.variety == null ? 0.6 : Math.max(0, Math.min(1, +o.variety));
    var info = slotRoles(o.format, o.formation || '4-3-3');
    var roles = info.roles;
    var cands = await candidates(th);
    if (cands.length < roles.length) throw new Error('Not enough players for that theme yet');

    // fill the scarcest roles first so a great centre-back is not spent in midfield
    var order = roles.map(function (r, i) { return i; });
    var SCARCE = { GK: 0, CB: 1, WB: 2, FB: 2, DM: 3, ST: 4, W: 5, AM: 6, CM: 7 };
    order.sort(function (a, b) { return (SCARCE[roles[a]] - SCARCE[roles[b]]) || (rnd() - 0.5); });
    var used = {}, picks = new Array(roles.length), meta = [];
    var topN = 1 + Math.round(variety * 5);
    order.forEach(function (si) {
      var role = roles[si];
      var ranked = [];
      cands.forEach(function (c) {
        if (used[c.p.q]) return;
        var f = fit(c.p, role); if (f < 0.45) return;
        ranked.push({ c: c, f: f, v: c.s * Math.pow(f, 2.5) });
      });
      if (!ranked.length) return;
      ranked.sort(function (a, b) { return b.v - a.v; });
      var pool = ranked.slice(0, topN), wsum = 0;
      pool.forEach(function (x, i) { x.w = Math.pow(0.62, i); wsum += x.w; });
      var r = rnd() * wsum, pick = pool[0];
      for (var i = 0; i < pool.length; i++) { r -= pool[i].w; if (r <= 0) { pick = pool[i]; break; } }
      used[pick.c.p.q] = 1; picks[si] = pick.c.p;
      meta[si] = { slot: si, role: role, fit: Math.round(pick.f * 100) / 100, score: Math.round(pick.c.s) };
    });

    // subs: the best of the rest, a keeper first
    var subs = [];
    var nSubs = o.subs === true ? info.maxSubs : Math.max(0, Math.min(info.maxSubs, +o.subs || 0));
    if (nSubs) {
      var rest = cands.filter(function (c) { return !used[c.p.q]; }).sort(function (a, b) { return b.s - a.s; });
      var gk = rest.filter(function (c) { return isKeeper(c.p); })[0];
      if (gk) { subs.push(gk.p); used[gk.p.q] = 1; }
      rest.forEach(function (c) { if (subs.length < nSubs && !used[c.p.q] && !isKeeper(c.p) && fit(c.p, 'CM') + fit(c.p, 'ST') + fit(c.p, 'CB') > 0) { subs.push(c.p); used[c.p.q] = 1; } });
    }

    var all = picks.filter(Boolean).concat(subs);
    await Promise.all(all.map(stintsOf));
    var nationTeam = null;
    function toSlot(p) {
      var s = pickStint(p, th), nm = splitName(p.name, p.short);
      if (s && s.national && th.kind === 'nation' && !nationTeam) nationTeam = s.team;
      return { name: p.name, first: nm.first, last: nm.last, pid: p.id, teamId: s ? s.teamId : null, team: s ? s.team : '', from: s ? s.from : null, to: s ? s.to : null, side: 'home', national: s ? !!s.national : false };
    }
    var slots = picks.map(function (p) { return p ? toSlot(p) : null; });
    var subSlots = subs.map(toSlot);
    while (subSlots.length < info.maxSubs) subSlots.push(null);

    // captain: the greatest outfield player
    var cap = null, cs = -1;
    picks.forEach(function (p, i) { if (p && i > 0 && p.score > cs) { cs = p.score; cap = i; } });

    var patch = {
      mode: 'legends', format: info.format, formation: o.formation || '4-3-3',
      title: o.title || themeTitle(th, nationTeam), slots: slots, subs: subSlots.slice(0, info.maxSubs), captain: cap, kits: null, step: 'build'
    };
    Object.defineProperty(patch, 'meta', { value: { seed: seed, theme: th.id, data: DATA.greatness ? 'data-next' : 'wizard', picks: meta }, enumerable: false });
    return patch;
  }

  function surpriseThemes() {
    var out = [{ id: 'all-time', label: 'All-time greats', group: 'Classic' }];
    var cl = root.YXI_CLUBS, teams = cl ? (cl.teams || cl) : [];
    var NATIONS = ['England', 'Scotland', 'Wales', 'Northern Ireland', 'Republic of Ireland', 'Brazil', 'Argentina', 'France', 'Germany', 'Italy', 'Spain', 'Netherlands', 'Portugal'];
    var clubFallback = [['Q9617', 'Arsenal'], ['Q9616', 'Chelsea'], ['Q1130849', 'Liverpool'], ['Q18656', 'Manchester United'], ['Q50602', 'Manchester City'], ['Q18741', 'Tottenham Hotspur'], ['Q5794', 'Everton'], ['Q18716', 'Newcastle United'], ['Q19593', 'Celtic'], ['Q19597', 'Rangers'], ['Q7156', 'Barcelona'], ['Q8682', 'Real Madrid'], ['Q1543', 'AC Milan'], ['Q1422', 'Juventus'], ['Q15789', 'Bayern Munich'], ['Q81888', 'Ajax']];
    var clubs = teams.length ? teams.filter(function (t) { return NATIONS.indexOf(t.name) < 0 && !/^(USA|USSR|Mexico|Japan|South Korea|Nigeria|Cameroon|Ghana|Senegal|Ivory Coast|Colombia|Chile|Poland|Hungary|Uruguay|Croatia|Denmark|Sweden|Norway|Belgium)$/.test(t.name); }).map(function (t) { return [t.teamId, t.name]; }) : clubFallback;
    clubs.forEach(function (c) { out.push({ id: 'club:' + c[0], label: c[1], group: 'Club' }); });
    NATIONS.forEach(function (n) { out.push({ id: 'nation:' + n, label: n, group: 'Country' }); });
    [1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020].forEach(function (y) { out.push({ id: 'decade:' + y + 's', label: 'The ' + y + 's', group: 'Decade' }); });
    return out;
  }

  /* =====================================================================
     4. Make another + My XIs
     ===================================================================== */
  var MY_KEY = 'yourxi-my-xis-v1', MAX = 10;
  function readList() { try { var a = JSON.parse(root.localStorage.getItem(MY_KEY)); return Array.isArray(a) ? a : []; } catch (e) { return []; } }
  function writeList(a) {
    a.sort(function (x, y) { return y.savedAt - x.savedAt; });
    while (a.length > MAX) a.pop();
    try { root.localStorage.setItem(MY_KEY, JSON.stringify(a)); } catch (e) { toast('Could not save. Your browser storage is full.'); }
    try { root.dispatchEvent(new CustomEvent('yxt:myxis')); } catch (e) {}
    return a;
  }
  function sig(s) { var c = clone(s) || {}; delete c.step; delete c._xid; return JSON.stringify(c); }
  function summary(s) {
    var slots = (s.slots || []), filled = slots.filter(Boolean);
    return {
      title: (s.title || '').trim() || (s._printTitle || '').trim() || 'Untitled XI', formation: s.formation || '', filled: filled.length, total: slots.length,
      names: filled.slice(1, 4).map(function (x) { return x.last || x.name; }).filter(Boolean)
    };
  }
  function uid() { return 'xi' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6); }
  function saveXI(state, o) {
    o = o || {};
    var a = readList(), sg = sig(state), now = Date.now();
    var hit = a.filter(function (e) { return (state._xid && e.id === state._xid) || sig(e.state) === sg; })[0];
    if (hit) { hit.state = clone(state); hit.savedAt = o.at || now; Object.assign(hit, summary(state)); }
    else { hit = Object.assign({ id: state._xid || uid(), savedAt: o.at || now, state: clone(state) }, summary(state)); a.push(hit); }
    hit.state._xid = hit.id;
    writeList(a);
    return hit;
  }
  function listXIs() { return readList().map(function (e) { return { id: e.id, title: e.title, formation: e.formation, filled: e.filled, total: e.total, names: e.names, savedAt: e.savedAt }; }); }
  function openXI(id) { var e = readList().filter(function (x) { return x.id === id; })[0]; return e ? clone(e.state) : null; }
  function deleteXI(id) { var a = readList(), n = a.length; a = a.filter(function (x) { return x.id !== id; }); writeList(a); return a.length < n; }

  function duplicateXI(state, o) {
    o = o || {};
    var now = Date.now();
    var base = ((state && state.title) || '').trim().replace(/^copy of\s+/i, '').replace(/\s+\d+$/, '') || 'my XI';
    var titles = readList().map(function (e) { return (e.title || '').toLowerCase(); });
    var t = 'Copy of ' + base, n = 2;
    while (titles.indexOf(t.toLowerCase()) >= 0 && n < 99) t = 'Copy of ' + base + ' ' + (n++);
    if (t.length > 40) t = t.slice(0, 40).trim();
    var copy = clone(state) || {};
    delete copy._xid; copy.title = t; copy.step = 'build';
    if (o.store !== false) {
      saveXI(state, { at: now });
      var e = saveXI(copy, { at: now + 1 });
      copy._xid = e.id;
    }
    return copy;
  }

  function ago(t) {
    var d = (Date.now() - t) / 1000;
    if (d < 60) return 'Saved just now';
    if (d < 3600) return 'Saved ' + Math.floor(d / 60) + ' min ago';
    if (d < 86400) return 'Saved ' + Math.floor(d / 3600) + ' h ago';
    return 'Saved ' + new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }
  function mountMyXIs(el, o) {
    o = o || {};
    var confirmId = null;
    el.classList.add('yxt-my');
    function render() {
      var list = listXIs();
      if (!list.length) { el.innerHTML = '<p class="yxt-my-empty">' + esc(o.emptyText || 'Nothing saved yet. Tap Make another and both XIs land here.') + '</p>'; return; }
      el.innerHTML = '<ul class="yxt-my-list" role="list">' + list.map(function (e) {
        var meta = [e.formation, e.filled + ' of ' + e.total + ' picked', ago(e.savedAt)].filter(Boolean).join(' · ');
        var who = e.names && e.names.length ? e.names.join(', ') : '';
        var actions = confirmId === e.id
          ? '<span class="yxt-my-q">Delete this XI?</span><button type="button" class="yxt-btn yxt-btn--sm" data-a="yes" data-id="' + e.id + '">Delete</button><button type="button" class="yxt-btn yxt-btn--sm yxt-btn--ghost" data-a="no">Keep</button>'
          : '<button type="button" class="yxt-btn yxt-btn--sm" data-a="open" data-id="' + e.id + '">Open</button><button type="button" class="yxt-btn yxt-btn--sm yxt-btn--ghost" data-a="del" data-id="' + e.id + '" aria-label="Delete ' + esc(e.title) + '">Delete</button>';
        return '<li class="yxt-my-item"><div class="yxt-my-text"><span class="yxt-my-t">' + esc(e.title) + '</span><span class="yxt-my-m">' + esc(meta) + '</span>' + (who ? '<span class="yxt-my-n">' + esc(who) + '</span>' : '') + '</div><div class="yxt-my-a">' + actions + '</div></li>';
      }).join('') + '</ul><p class="yxt-my-cap">Up to ' + MAX + ' XIs, saved on this device.</p>';
    }
    function onClick(e) {
      var b = e.target.closest('button'); if (!b) return;
      var id = b.dataset.id, a = b.dataset.a;
      if (a === 'open') { var s = openXI(id); var entry = readList().filter(function (x) { return x.id === id; })[0]; if (s && o.onOpen) o.onOpen(s, entry); }
      else if (a === 'del') { confirmId = id; render(); var y = el.querySelector('[data-a="yes"]'); if (y) y.focus(); }
      else if (a === 'no') { confirmId = null; render(); }
      else if (a === 'yes') { var en = readList().filter(function (x) { return x.id === id; })[0]; deleteXI(id); confirmId = null; render(); if (o.onDelete && en) o.onDelete(en); }
    }
    function onStore(e) { if (!e || e.type === 'yxt:myxis' || e.key === MY_KEY) render(); }
    el.addEventListener('click', onClick);
    root.addEventListener('storage', onStore); root.addEventListener('yxt:myxis', onStore);
    render();
    return { refresh: render, destroy: function () { el.removeEventListener('click', onClick); root.removeEventListener('storage', onStore); root.removeEventListener('yxt:myxis', onStore); el.innerHTML = ''; } };
  }

  root.YXITools = {
    version: '1.0.0',
    config: config,
    createHistory: createHistory, mountHistoryButtons: mountHistoryButtons,
    makeShareImage: makeShareImage, share: share, shareLink: shareLink, copyText: copyText,
    surpriseXI: surpriseXI, surpriseThemes: surpriseThemes, loadPlayers: loadData,
    duplicateXI: duplicateXI, saveXI: saveXI, listXIs: listXIs, openXI: openXI, deleteXI: deleteXI, mountMyXIs: mountMyXIs,
    toast: toast,
    _internal: { fit: fit, slotRoles: slotRoles, parseTheme: parseTheme, packState: packState }
  };
})(typeof window !== 'undefined' ? window : this);
